export type StoredTokens = {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
};

export const decodeTokenPayload = (token: string): Record<string, unknown> => {
  const segments = token.split('.');
  if (segments.length !== 3 || segments.some((segment) => !/^[A-Za-z0-9_-]+$/.test(segment))) {
    throw new Error('認証トークンの形式が不正です');
  }
  const encoded = segments[1];
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(encoded.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('認証トークンの形式が不正です');
  return payload as Record<string, unknown>;
};

const tokenExpiryMs = (token: string): number => {
  const exp = decodeTokenPayload(token).exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return Number.NaN;
  return exp * 1000;
};

export const parseStoredTokens = (value: unknown): StoredTokens | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Partial<StoredTokens>;
  if (
    typeof candidate.accessToken !== 'string'
    || candidate.accessToken.length === 0
    || typeof candidate.idToken !== 'string'
    || candidate.idToken.length === 0
    || typeof candidate.expiresAt !== 'number'
    || !Number.isFinite(candidate.expiresAt)
    || (candidate.refreshToken !== undefined && (typeof candidate.refreshToken !== 'string' || candidate.refreshToken.length === 0))
  ) return null;
  try {
    if (!Number.isFinite(tokenExpiryMs(candidate.idToken)) || !Number.isFinite(tokenExpiryMs(candidate.accessToken))) return null;
  } catch {
    return null;
  }
  return candidate as StoredTokens;
};

export const storedTokenExpiryMs = (tokens: StoredTokens): number => {
  return Math.min(tokens.expiresAt, tokenExpiryMs(tokens.idToken), tokenExpiryMs(tokens.accessToken));
};

export const isStoredTokenCurrent = (tokens: StoredTokens, now = Date.now()) => {
  try {
    const expiresAt = storedTokenExpiryMs(tokens);
    return Number.isFinite(expiresAt) && expiresAt > now;
  } catch {
    return false;
  }
};

export const resolveStoredTokens = async (
  existing: StoredTokens | null,
  refreshTokens: (current: StoredTokens) => Promise<StoredTokens>,
): Promise<StoredTokens | null> => {
  if (!existing) return null;
  if (isStoredTokenCurrent(existing)) return existing;
  if (!existing.refreshToken) return null;
  try {
    const refreshed = await refreshTokens(existing);
    return isStoredTokenCurrent(refreshed) ? refreshed : null;
  } catch {
    return null;
  }
};

export const refreshStoredTokensOrClear = async (
  current: StoredTokens,
  refreshTokens: (tokens: StoredTokens) => Promise<StoredTokens>,
  clearTokens: () => void,
): Promise<StoredTokens> => {
  try {
    const refreshed = await refreshTokens(current);
    if (!isStoredTokenCurrent(refreshed)) throw new Error('更新されたログインセッションが無効です');
    return refreshed;
  } catch (error) {
    clearTokens();
    throw error;
  }
};
